/* eslint-disable camelcase */

import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import TextField from '../BasicFields/TextField'
import SelectionOption from './SelectionOption'
import Button from '../../ui/Button'
import { updateErrors } from '../SectionField'
import TrashCanIcon from '../../ui/icons/TrashCanIcon'
import DuplicateIcon from '../../ui/icons/DuplicateIcon'
import AddConditionButton from '../AddConditionButton'
import { idGenerator, isNewRecord } from '../../../utils/idHelpers'
import { dispatchConfirmDelete } from '../../shared/ConfirmModal'
import Select from '../../ui/Select/Select'
import Switch from '../../ui/Switch'
import QuestionSettings from './QuestionSettings'

const OPTIONS_ORDERS = [
  { label: 'Manuel', value: 'position' },
  { label: 'Alphabétique', value: 'alphabetical' },
  { label: 'Les plus choisis', value: 'usage' }
]

/*
 * TODO: Simplify events handle with useReducer
 * TODO: Simplify component state with useContext
 * TODO: Use react-uid to generate unique ids
 */
const Selection = ({
  question, getErrors, handleQuestionChange, handleQuestionDuplicate,
  handleQuestionRemove, dragHandleProps, hasConditionals, hasDynamicOptions, onAddCondition
}) => {
  const multi = question.context === 'selection_multi'
  const dynamicOptionsEnabled = hasDynamicOptions && !multi
  const [state, setState] = useState({
    id: question.id,
    context: question.context,
    position: question.position,
    title: question.title,
    mandatory: question.mandatory,
    allow_option_creation: question.allow_option_creation,
    options_order: question.options_order || 'position',
    options: question.options,
    removed_option_ids: [],
    errors: question.errors || {}
  })

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      errors: updateErrors(prevState.errors, getErrors)
    }))
  }, [getErrors])

  useEffect(() => {
    handleQuestionChange(state)
  }, [state, handleQuestionChange])

  const handleChange = useCallback(event => {
    const { name, value } = event.target
    setState(prevState => ({
      ...prevState,
      [name]: value
    }))
  }, [])

  const handleMandatoryChange = useCallback(event => {
    setState(prevState => ({
      ...prevState,
      mandatory: event.target.checked
    }))
  }, [])

  const handleAllowOptionCreationChange = useCallback(event => {
    setState(prevState => ({ ...prevState, allow_option_creation: event.target.checked }))
  }, [])

  const handleOptionsOrderChange = useCallback(option => {
    setState(prevState => ({ ...prevState, options_order: option.value }))
  }, [])

  const handleClickNewOption = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      options: prevState.options.concat({ id: idGenerator(), content: '', default: false })
    }))
  }, [])

  const removeOption = useCallback(optionId => {
    setState(prevState => {
      let newlyRemovedOption = []
      const updatedOptions = prevState.options.filter(option => option.id !== optionId)

      if (!isNewRecord(optionId))
        newlyRemovedOption = prevState.removed_option_ids.concat(optionId)

      return {
        ...prevState,
        options: updatedOptions,
        removed_option_ids: newlyRemovedOption
      }
    })
  }, [])

  const handleRemoveOption = useCallback(optionId => () => {
    if (isNewRecord(optionId)) {
      removeOption(optionId)
      return
    }
    const deleteMessage = 'Tous les résultats associés à cette option seront perdus.'
    dispatchConfirmDelete('Supprimer l\'option', deleteMessage, () => removeOption(optionId))
  }, [removeOption])

  const handleOptionChange = useCallback(index => option => {
    setState(prevState => {
      const updatedOptions = [...prevState.options]
      updatedOptions[index] = option
      if (option.default && !multi) {
        updatedOptions.forEach((opt, optionIndex) => {
          if (optionIndex !== index)
            updatedOptions[optionIndex] = { ...opt, default: false }
        })
      }
      return {
        ...prevState,
        options: updatedOptions
      }
    })
  }, [])

  return (
    <div className="nested-fields ml-2 ml-md-4">
      <div className="d-flex">
        <div className="mr-auto">
          <i className="fas fa-arrows-alt c-pointer" {...dragHandleProps} />
          {
            multi ? ' Question avec choix multiples ' : ' Question avec choix unique '
          }
          <DuplicateIcon
            className="tw:text-gray-500 tw:hover:text-gray-700 c-pointer"
            onClick={handleQuestionDuplicate(state.id)}
          />
        </div>
        <TrashCanIcon
          className="tw:text-gray-500 tw:hover:text-gray-700 c-pointer"
          onClick={handleQuestionRemove(state.id)}
        />
      </div>
      <TextField
        name="title"
        value={state.title}
        placeholder="Titre de la question..."
        errors={state.errors.title}
        onChange={handleChange}
      />
      {state.options.map((option, index) => (
        <SelectionOption
          key={option.id}
          option={option}
          handleOptionChange={handleOptionChange(index)}
          handleRemoveOption={handleRemoveOption(option.id)}
          multi={multi}
        />
      ))}
      {
        state.errors && (state.errors.option_ids || state.errors.option_id) ? (
          <small className="text-danger">
            {(state.errors.option_ids || state.errors.option_id)[0]}
            <br />
          </small>
        ) : null
      }
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Button variant="neutral" icon="plus" onClick={handleClickNewOption}>
          Ajouter un choix
        </Button>
        {hasConditionals ? <AddConditionButton onClick={onAddCondition} /> : null}
      </div>
      <QuestionSettings
        id={state.id}
        mandatory={state.mandatory}
        aside={dynamicOptionsEnabled ? (
          <div className="tw:flex tw:items-center tw:gap-2">
            <label className="tw:mb-0! tw:text-sm tw:text-gray-700" htmlFor={`options-order-${state.id}`}>
              Ordre d&apos;affichage
            </label>
            <Select
              className="tw:w-44"
              inputId={`options-order-${state.id}`}
              isSearchable={false}
              options={OPTIONS_ORDERS}
              value={OPTIONS_ORDERS.find(order => order.value === state.options_order)}
              menuPortal
              onChange={handleOptionsOrderChange}
            />
          </div>
        ) : null}
        onMandatoryChange={handleMandatoryChange}
      >
        {dynamicOptionsEnabled ? (
          <Switch
            id={`allow-option-creation-${state.id}`}
            checked={state.allow_option_creation}
            label="Autoriser l'ajout de choix par les opérateurs"
            onChange={handleAllowOptionCreationChange}
          />
        ) : null}
      </QuestionSettings>
    </div>
  )
}

Selection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  dragHandleProps: PropTypes.object.isRequired,
  getErrors: PropTypes.func.isRequired,
  handleQuestionChange: PropTypes.func.isRequired,
  handleQuestionDuplicate: PropTypes.func.isRequired,
  handleQuestionRemove: PropTypes.func.isRequired,
  hasConditionals: PropTypes.bool,
  hasDynamicOptions: PropTypes.bool,
  onAddCondition: PropTypes.func,
  question: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    context: PropTypes.string.isRequired,
    position: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    mandatory: PropTypes.bool.isRequired,
    allow_option_creation: PropTypes.bool,
    options_order: PropTypes.oneOf(OPTIONS_ORDERS.map(order => order.value)),
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      content: PropTypes.string.isRequired
    })).isRequired,
    errors: PropTypes.shape({
      title: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired
}

Selection.defaultProps = {
  hasConditionals: false,
  hasDynamicOptions: false,
  onAddCondition: null
}

export default Selection